import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { ProviderLogo } from "../shared/ProviderLogo";
import { PROVIDER_REASONING_LEVELS, reasoningLabel } from "../../lib/reasoning";

const PROVIDERS = ["openai", "anthropic", "google", "grok", "openrouter", "deepseek", "meta"];
const PROVIDER_LABEL: Record<string, string> = {
  openai: "OpenAI", anthropic: "Anthropic", google: "Google", grok: "Grok", openrouter: "OpenRouter", deepseek: "DeepSeek", meta: "Meta",
};

interface AvailableModel {
  id: string;
  provider: string;
  model_name: string;
  label: string;
  thinking_levels: string[];
}

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-secondary)",
  display: "block",
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
};

export interface ModelSelectorValue {
  provider: string;
  model_name: string;
  temperature: number;
  reasoning_effort: string;
  // text.verbosity (OpenAI gpt-5.6+). "default" = padrão do modelo (vira null na API).
  verbosity?: string;
  maxTokens?: number;
}

const VERBOSITY_OPTIONS: [string, string][] = [
  ["default", "Padrão do modelo"],
  ["low", "Baixa"],
  ["medium", "Média"],
  ["high", "Alta"],
];

export function ModelSelector({
  value,
  onChange,
  showMaxTokens = true,
  showVerbosity = false,
  compact = false,
}: {
  value: ModelSelectorValue;
  onChange: (next: ModelSelectorValue) => void;
  showMaxTokens?: boolean;
  showVerbosity?: boolean;
  compact?: boolean;
}) {
  const { provider, model_name: modelName, temperature, reasoning_effort: reasoningEffort } = value;
  const maxTokens = value.maxTokens ?? 4096;

  const [providerOpen, setProviderOpen] = useState(false);
  const providerRef = useRef<HTMLDivElement>(null);

  const { data: catalog = [] } = useQuery<AvailableModel[]>({
    queryKey: ["available-models"],
    queryFn: () => api.get("/dev/available-models"),
  });

  useEffect(() => {
    if (!providerOpen) return;
    function onDoc(e: MouseEvent) {
      if (providerRef.current && !providerRef.current.contains(e.target as Node)) {
        setProviderOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [providerOpen]);

  const providerModels = catalog.filter((m) => m.provider === provider);
  const currentInCatalog = providerModels.some((m) => m.model_name === modelName);
  // Modelo salvo que não está no catálogo aparece como "(atual)" para não quebrar.
  const modelOptions: AvailableModel[] = !modelName || currentInCatalog
    ? providerModels
    : [
        { id: "__current__", provider, model_name: modelName, label: `${modelName} (atual)`, thinking_levels: PROVIDER_REASONING_LEVELS[provider] ?? ["off"] },
        ...providerModels,
      ];
  const selectedModel = modelOptions.find((m) => m.model_name === modelName) ?? null;

  const reasoningTokens = selectedModel?.thinking_levels?.length ? selectedModel.thinking_levels : ["off"];
  const reasoningOptions = reasoningTokens.includes(reasoningEffort) ? reasoningTokens : [reasoningEffort, ...reasoningTokens];

  function emit(patch: Partial<ModelSelectorValue>) {
    const next: ModelSelectorValue = { ...value, ...patch };
    if (!showMaxTokens) delete next.maxTokens;
    onChange(next);
  }

  function handleProviderChange(newProvider: string) {
    const first = catalog.find((m) => m.provider === newProvider);
    const levels = first?.thinking_levels ?? [];
    emit({
      provider: newProvider,
      model_name: first?.model_name ?? "",
      reasoning_effort: levels.length ? (levels.includes("off") ? "off" : levels[0]) : "off",
    });
    setProviderOpen(false);
  }

  function handleModelChange(name: string) {
    const m = catalog.find((x) => x.provider === provider && x.model_name === name);
    const levels = m?.thinking_levels ?? [];
    const patch: Partial<ModelSelectorValue> = { model_name: name };
    if (levels.length && !levels.includes(reasoningEffort)) {
      patch.reasoning_effort = levels.includes("off") ? "off" : levels[0];
    }
    emit(patch);
  }

  if (compact) {
    const cLabel: React.CSSProperties = { fontSize: 10, color: "var(--ink-soft)", marginBottom: 3 };
    const cInput: React.CSSProperties = { ...inputStyle, padding: "6px 8px", fontSize: 12 };
    return (
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 130px", minWidth: 110, display: "flex", flexDirection: "column" }}>
          <label style={cLabel}>Provedor</label>
          <div ref={providerRef} style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setProviderOpen((o) => !o)}
              style={{ ...cInput, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", textAlign: "left", width: "100%" }}
            >
              <ProviderLogo provider={provider} size={14} />
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{PROVIDER_LABEL[provider]}</span>
              <span style={{ color: "var(--ink-muted)", fontSize: 10 }}>▾</span>
            </button>
            {providerOpen && (
              <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 20, background: "var(--surface-card)", border: "1px solid var(--line-strong)", borderRadius: 8, overflow: "hidden", boxShadow: "var(--shadow-pop)" }}>
                {PROVIDERS.map((p) => (
                  <button key={p} type="button" onClick={() => handleProviderChange(p)}
                    style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "6px 10px", border: "none", cursor: "pointer", fontSize: 12, textAlign: "left", background: p === provider ? "var(--line)" : "transparent", color: "var(--ink)" }}>
                    <ProviderLogo provider={p} size={14} />
                    {PROVIDER_LABEL[p]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ flex: "2 1 150px", minWidth: 130, display: "flex", flexDirection: "column" }}>
          <label style={cLabel}>Modelo</label>
          <select style={cInput} value={modelName} onChange={(e) => handleModelChange(e.target.value)}>
            {modelOptions.map((m) => <option key={m.model_name} value={m.model_name}>{m.label}</option>)}
          </select>
        </div>
        <div style={{ flex: "1 1 110px", minWidth: 95, display: "flex", flexDirection: "column" }}>
          <label style={cLabel}>Esforço</label>
          <select style={cInput} value={reasoningEffort} onChange={(e) => emit({ reasoning_effort: e.target.value })}>
            {reasoningOptions.map((t) => <option key={t} value={t}>{reasoningLabel(t)}</option>)}
          </select>
        </div>
        {showVerbosity && provider === "openai" && (
          <div style={{ flex: "1 1 110px", minWidth: 95, display: "flex", flexDirection: "column" }}>
            <label style={cLabel}>Verbosidade</label>
            <select style={cInput} value={value.verbosity ?? "default"} onChange={(e) => emit({ verbosity: e.target.value })}>
              {VERBOSITY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        )}
        <div style={{ width: 78, display: "flex", flexDirection: "column" }}>
          <label style={cLabel}>Temp.</label>
          <input
            type="number"
            min={0}
            max={2}
            step={0.05}
            value={temperature}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              emit({ temperature: Number.isNaN(v) ? 0 : Math.min(2, Math.max(0, v)) });
            }}
            style={cInput}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <label style={labelStyle}>Provedor</label>
        <div ref={providerRef} style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setProviderOpen((o) => !o)}
            style={{ ...inputStyle, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textAlign: "left" }}
          >
            <ProviderLogo provider={provider} size={18} />
            <span style={{ flex: 1 }}>{PROVIDER_LABEL[provider]}</span>
            <span style={{ color: "var(--ink-muted)", fontSize: 11 }}>▾</span>
          </button>
          {providerOpen && (
            <div
              style={{
                position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 20,
                background: "var(--surface-card)", border: "1px solid var(--line-strong)", borderRadius: 8,
                overflow: "hidden", boxShadow: "var(--shadow-pop)",
              }}
            >
              {PROVIDERS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => handleProviderChange(p)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%",
                    padding: "8px 12px", border: "none", cursor: "pointer", fontSize: 13,
                    textAlign: "left",
                    background: p === provider ? "var(--line)" : "transparent",
                    color: "var(--ink)",
                  }}
                >
                  <ProviderLogo provider={p} size={18} />
                  {PROVIDER_LABEL[p]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label style={labelStyle}>Modelo</label>
        {modelOptions.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--ink-soft)", padding: "8px 0" }}>
            Nenhum modelo disponível para este provedor. Cadastre no Painel Dev → Modelos Disponíveis.
          </div>
        ) : (
          <select style={inputStyle} value={modelName} onChange={(e) => handleModelChange(e.target.value)}>
            {modelOptions.map((m) => (
              <option key={m.model_name} value={m.model_name}>{m.label}</option>
            ))}
          </select>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label style={labelStyle}>Esforço de raciocínio</label>
        <select
          style={inputStyle}
          value={reasoningEffort}
          onChange={(e) => emit({ reasoning_effort: e.target.value })}
        >
          {reasoningOptions.map((t) => (
            <option key={t} value={t}>{reasoningLabel(t)}</option>
          ))}
        </select>
        <span style={{ fontSize: 11, color: "var(--ink-muted)", marginTop: 4 }}>
          Opções definidas pelos níveis que o modelo suporta (cadastrados no Painel Dev).
        </span>
      </div>

      {showVerbosity && provider === "openai" && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label style={labelStyle}>Verbosidade</label>
          <select
            style={inputStyle}
            value={value.verbosity ?? "default"}
            onChange={(e) => emit({ verbosity: e.target.value })}
          >
            {VERBOSITY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span style={{ fontSize: 11, color: "var(--ink-muted)", marginTop: 4 }}>
            Tamanho padrão das respostas (gpt-5.6+). Instruções do prompt têm prioridade.
          </span>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>Temperature</label>
          <span style={{ fontSize: 12, color: "var(--ink)" }}>{temperature.toFixed(2)}</span>
        </div>
        <input
          type="range"
          min={0}
          max={2}
          step={0.05}
          value={temperature}
          onChange={(e) => emit({ temperature: parseFloat(e.target.value) })}
          style={{ width: "100%", accentColor: "var(--brand)" }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--ink-muted)", marginTop: 4 }}>
          <span>0.0</span>
          <span>2.0</span>
        </div>
      </div>

      {showMaxTokens && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label style={labelStyle}>Máximo de tokens</label>
          <input
            type="number"
            style={inputStyle}
            value={maxTokens}
            min={1}
            step={256}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              emit({ maxTokens: Number.isNaN(v) ? 0 : v });
            }}
            spellCheck={false}
          />
          <span style={{ fontSize: 11, color: "var(--ink-muted)", marginTop: 4 }}>
            Tamanho máximo da resposta gerada.
          </span>
        </div>
      )}
    </div>
  );
}
